import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { FirmOfferDetailDto, SalesOrderLineItemDto } from '../types/salesOrder.types';
import { lineWeightKg, totalLineWeightKg } from '../utils/lineWeight';
import { evaluateLinesTonnage } from '../utils/tonnageGate';

// FO-SOI: FO balance dashboard. The headline is the TOTAL requested-vs-FO-balance
// gauge — that total is the actual hard gate enforced at submit (86d3cfnua); when it
// is over, posting is blocked. Below it, each grade gets a card with its running
// requested kg vs the FO's per-grade remaining kg as an informational guideline split
// (per-grade is NOT enforced — sales confirmed grade buckets are only a guideline).

interface Props {
  firmOffer: FirmOfferDetailDto | null;
  lineItems: SalesOrderLineItemDto[];
  /** FO detail still loading — show skeleton chips instead of nothing (86d3a1q88). */
  isLoading?: boolean;
}

const GRADE_CODE_TO_PNS: Record<string, { pnsLabel: string; uiLabel: string }> = {
  '33': { pnsLabel: '230', uiLabel: 'G33' },
  '40': { pnsLabel: '280', uiLabel: 'G40' },
  '60': { pnsLabel: '420', uiLabel: 'G60' },
  '75': { pnsLabel: '520', uiLabel: 'G75' },
  '80': { pnsLabel: '550', uiLabel: 'G80' },
};

const NEAR_LIMIT_THRESHOLD = 0.8; // 80% triggers amber

function formatKg(v: number): string {
  return v.toLocaleString('en-PH', { maximumFractionDigits: 2 });
}

function formatPct(v: number): string {
  return `${(v * 100).toFixed(0)}%`;
}

export function PerGradeBalanceChips({ firmOffer, lineItems, isLoading }: Props) {
  if (!firmOffer) {
    if (isLoading) {
      return (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-40" />
          ))}
        </div>
      );
    }
    return null;
  }

  // Running requested kg per PNS grade — pieces × kg per piece, the weight the
  // submit check uses (14ykpqtv3qw; the stored weightKg was the quantity for a
  // kg-labelled line).
  const requestedByPns: Record<string, number> = {
    '230': 0,
    '280': 0,
    '420': 0,
    '520': 0,
    '550': 0,
  };
  for (const line of lineItems) {
    const gradeCode = line.gradeCode;
    if (!gradeCode) continue;
    const mapping = GRADE_CODE_TO_PNS[gradeCode];
    if (!mapping) continue;
    requestedByPns[mapping.pnsLabel] =
      (requestedByPns[mapping.pnsLabel] ?? 0) + (lineWeightKg(line) ?? 0);
  }

  const grades = Object.entries(GRADE_CODE_TO_PNS)
    .map(([gradeCode, m]) => {
      const available = firmOffer.perGradeBalanceKg[m.pnsLabel] ?? 0;
      const requested = requestedByPns[m.pnsLabel] ?? 0;
      return { gradeCode, mapping: m, available, requested };
    })
    // Hide grades the FO doesn't authorize AND aren't being used.
    .filter((g) => g.available > 0 || g.requested > 0);

  if (grades.length === 0) return null;

  const overGrades = grades.filter((g) => g.requested > g.available);
  const anyOver = overGrades.length > 0;

  // TOTAL — the actual hard gate (86d3cfnua). Requested tonnage summed across every
  // line vs the FO's total remaining balance (firmOffer.balanceKg), with the server's
  // rounding allowance (14ykpqtv3qx, utils/tonnageGate). Over => submit blocked.
  const totalRequested = totalLineWeightKg(lineItems);
  const totalAvailable = firmOffer.balanceKg ?? 0;
  const tonnage = evaluateLinesTonnage(lineItems, totalAvailable);
  const totalRatio = totalAvailable > 0 ? totalRequested / totalAvailable : 0;
  const totalOver = tonnage.isRefused;
  // Past the balance by no more than per-piece rounding: it posts, so it reads calm — never red or amber.
  const withinRounding = !totalOver && tonnage.overBalanceKg > 0;
  const totalNear = !totalOver && !withinRounding && totalRatio >= NEAR_LIMIT_THRESHOLD;
  const totalRemaining = totalAvailable - totalRequested;

  return (
    <div className="space-y-2">
      {/* Per-grade is a guideline split, not enforced — keep it calm/informational. */}
      {anyOver && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <span className="font-semibold">
              {overGrades.length}{' '}
              {overGrades.length === 1 ? 'grade is' : 'grades are'} over the Firm Offer
              allocation.
            </span>{' '}
            You can still post — only the total balance matters.
          </div>
        </div>
      )}

      {/* Exactly one column per card (Total + each shown grade) so they always span
          the full row evenly, regardless of container width or grade count. */}
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `repeat(${grades.length + 1}, minmax(0, 1fr))`,
        }}
      >
        {/* TOTAL — the enforced gate. Same card footprint as the per-grade chips so it
            aligns in the grid; distinct via filled bg + inset ring + uppercase label. */}
        <div
          role={totalOver ? 'alert' : undefined}
          className={cn(
            'flex flex-col gap-1.5 rounded-md border bg-muted/60 p-2.5 ring-1 ring-inset ring-border',
            totalOver && 'border-destructive/40 bg-destructive/10 ring-destructive/30',
            totalNear && 'border-warning/40 bg-warning/10 ring-warning/30',
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
              Total
            </span>
            {totalOver ? (
              <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-label="Over balance" />
            ) : totalNear ? (
              <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-label="Near balance" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-label="Within balance" />
            )}
          </div>
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="font-mono tabular-nums">
              <span
                className={cn(
                  'font-semibold',
                  totalOver ? 'text-destructive' : totalNear ? 'text-warning' : 'text-foreground',
                )}
              >
                {formatKg(totalRequested)}
              </span>
              <span className="text-muted-foreground"> / {formatKg(totalAvailable)} kg</span>
            </span>
            <span className="text-[10px] font-mono tabular-nums text-muted-foreground">
              {formatPct(Math.min(totalRatio, 1))}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                totalOver ? 'bg-destructive' : totalNear ? 'bg-warning' : 'bg-success',
              )}
              style={{ width: `${Math.min(totalRatio, 1) * 100}%` }}
            />
          </div>
          <div className="text-[11px] tabular-nums">
            {totalOver ? (
              <span className="font-semibold text-destructive">
                Over by {formatKg(tonnage.overBalanceKg)} kg · blocks posting
              </span>
            ) : withinRounding ? (
              <span
                className="cursor-help text-muted-foreground"
                title={`Allowed: up to ${formatKg(tonnage.allowanceKg)} kg of per-piece weight rounding (half a gram per bar).`}
              >
                {formatKg(tonnage.overBalanceKg)} kg over the FO, within rounding
              </span>
            ) : (
              <span className="text-muted-foreground">
                {formatKg(totalRemaining)} kg remaining
              </span>
            )}
          </div>
        </div>
        {grades.map((g) => {
          const ratio = g.available > 0 ? g.requested / g.available : 0;
          // Per-grade is an informational guideline, not an enforced gate — so it tops out
          // at amber (`warning`), never red. Red (`destructive`) is reserved for the Total
          // card, the only state that actually blocks posting (86d3cfnua).
          const isOver = g.requested > g.available;
          const remaining = g.available - g.requested;
          return (
            <div
              key={g.gradeCode}
              className={cn(
                'flex flex-col gap-1.5 rounded-md border bg-background p-2.5',
                isOver && 'border-warning/30 bg-warning/10',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded font-mono text-xs font-semibold',
                    isOver ? 'text-warning' : 'text-foreground',
                  )}
                >
                  {g.mapping.uiLabel}
                </span>
                {isOver ? (
                  <AlertTriangle
                    className="h-3.5 w-3.5 text-warning"
                    aria-label="Over allocation"
                  />
                ) : (
                  <CheckCircle2
                    className="h-3.5 w-3.5 text-success"
                    aria-label="Within allocation"
                  />
                )}
              </div>
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-mono tabular-nums">
                  <span
                    className={cn(
                      'font-semibold',
                      isOver ? 'text-warning' : 'text-foreground',
                    )}
                  >
                    {formatKg(g.requested)}
                  </span>
                  <span className="text-muted-foreground">
                    {' '}
                    / {formatKg(g.available)} kg
                  </span>
                </span>
                <span className="text-[10px] font-mono tabular-nums text-muted-foreground">
                  {formatPct(Math.min(ratio, 1))}
                </span>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    isOver ? 'bg-warning' : 'bg-success',
                  )}
                  style={{ width: `${Math.min(ratio, 1) * 100}%` }}
                />
              </div>
              <div className="text-[11px] text-muted-foreground tabular-nums">
                {isOver ? (
                  <span className="font-semibold text-warning">
                    Over allocation by {formatKg(Math.abs(remaining))} kg
                  </span>
                ) : (
                  <>{formatKg(remaining)} kg remaining</>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
